// Helper functions for payment management

/**
 * Format amount as currency
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'HKD')
 * @param {string} locale - Locale for formatting (default: 'zh-HK')
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currency = 'HKD', locale = 'zh-HK') => {
  try {
    if (typeof amount !== 'number' || isNaN(amount)) {
      return 'HKD 0.00';
    }

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `HKD ${amount.toFixed(2)}`;
  }
};

/**
 * Calculate total amount from payment records
 * @param {Array} paymentRecords - Array of payment records
 * @param {string} status - Optional status filter
 * @param {string} studentId - Optional student ID filter
 * @param {string} startDate - Optional start date filter
 * @param {string} endDate - Optional end date filter
 * @returns {number} - Total amount
 */
export const calculateTotalAmount = (paymentRecords, status = null, studentId = null, startDate = null, endDate = null) => {
  try {
    if (!Array.isArray(paymentRecords) || paymentRecords.length === 0) {
      return 0;
    }

    // Filter records based on parameters
    let filteredRecords = paymentRecords;

    if (status) {
      filteredRecords = filteredRecords.filter(record => record.status === status);
    }

    if (studentId) {
      filteredRecords = filteredRecords.filter(record => record.studentId === studentId);
    }

    if (startDate && endDate) {
      filteredRecords = filteredRecords.filter(record => {
        if (record.paymentDate) {
          const paymentDate = new Date(record.paymentDate);
          return paymentDate >= new Date(startDate) && paymentDate <= new Date(endDate);
        }
        return false;
      });
    }

    // Calculate total amount
    const total = filteredRecords.reduce((sum, record) => {
      return sum + (record.amount || 0);
    }, 0);

    return Math.round(total * 100) / 100; // Round to 2 decimal places
  } catch (error) {
    console.error('Error calculating total amount:', error);
    return 0;
  }
};

/**
 * Get payment statistics
 * @param {Array} paymentRecords - Array of payment records
 * @returns {Object} - Payment statistics
 */
export const getPaymentStats = (paymentRecords) => {
  try {
    if (!Array.isArray(paymentRecords) || paymentRecords.length === 0) {
      return {
        total: 0,
        paid: 0,
        pending: 0,
        overdue: 0,
        totalAmount: 0,
        paidAmount: 0,
        pendingAmount: 0,
        overdueAmount: 0
      };
    }

    const total = paymentRecords.length;
    const paid = paymentRecords.filter(record => record.status === 'paid').length;
    const pending = paymentRecords.filter(record => record.status === 'pending').length;
    const overdue = paymentRecords.filter(record => record.status === 'overdue').length;

    const totalAmount = calculateTotalAmount(paymentRecords);
    const paidAmount = calculateTotalAmount(paymentRecords, 'paid');
    const pendingAmount = calculateTotalAmount(paymentRecords, 'pending');
    const overdueAmount = calculateTotalAmount(paymentRecords, 'overdue');

    return {
      total,
      paid,
      pending,
      overdue,
      totalAmount,
      paidAmount,
      pendingAmount,
      overdueAmount
    };
  } catch (error) {
    console.error('Error getting payment stats:', error);
    return {
      total: 0,
      paid: 0,
      pending: 0,
      overdue: 0,
      totalAmount: 0,
      paidAmount: 0,
      pendingAmount: 0,
      overdueAmount: 0
    };
  }
};

/**
 * Get payment summary for a student
 * @param {Array} paymentRecords - Array of payment records
 * @param {string} studentId - Student ID
 * @returns {Object} - Student payment summary
 */
export const getStudentPaymentSummary = (paymentRecords, studentId) => {
  try {
    const studentPayments = paymentRecords.filter(record => record.studentId === studentId);
    
    if (studentPayments.length === 0) {
      return {
        totalItems: 0,
        paidItems: 0,
        pendingItems: 0,
        overdueItems: 0,
        totalAmount: 0,
        paidAmount: 0,
        pendingAmount: 0,
        overdueAmount: 0,
        paymentRate: 0
      };
    }

    const totalItems = studentPayments.length;
    const paidItems = studentPayments.filter(record => record.status === 'paid').length;
    const pendingItems = studentPayments.filter(record => record.status === 'pending').length;
    const overdueItems = studentPayments.filter(record => record.status === 'overdue').length;

    const totalAmount = calculateTotalAmount(studentPayments);
    const paidAmount = calculateTotalAmount(studentPayments, 'paid');
    const pendingAmount = calculateTotalAmount(studentPayments, 'pending');
    const overdueAmount = calculateTotalAmount(studentPayments, 'overdue');

    const paymentRate = totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;

    return {
      totalItems,
      paidItems,
      pendingItems,
      overdueItems,
      totalAmount,
      paidAmount,
      pendingAmount,
      overdueAmount,
      paymentRate: Math.round(paymentRate * 100) / 100
    };
  } catch (error) {
    console.error('Error getting student payment summary:', error);
    return {
      totalItems: 0,
      paidItems: 0,
      pendingItems: 0,
      overdueItems: 0,
      totalAmount: 0,
      paidAmount: 0,
      pendingAmount: 0,
      overdueAmount: 0,
      paymentRate: 0
    };
  }
};

/**
 * Check if payment is overdue
 * @param {string} dueDate - Due date
 * @param {string} paymentDate - Payment date (optional)
 * @returns {boolean} - True if payment is overdue
 */
export const isPaymentOverdue = (dueDate, paymentDate = null) => {
  try {
    if (paymentDate) {
      // If payment is made, it's not overdue
      return false;
    }

    const today = new Date();
    const due = new Date(dueDate);
    
    return today > due;
  } catch (error) {
    console.error('Error checking if payment is overdue:', error);
    return false;
  }
};

/**
 * Get payment status color for UI
 * @param {string} status - Payment status
 * @returns {string} - CSS color class or hex color
 */
export const getPaymentStatusColor = (status) => {
  switch (status) {
    case 'paid':
      return '#28a745'; // Green
    case 'pending':
      return '#ffc107'; // Yellow
    case 'overdue':
      return '#dc3545'; // Red
    default:
      return '#6c757d'; // Gray
  }
};

/**
 * Get payment status text in Chinese
 * @param {string} status - Payment status
 * @returns {string} - Chinese status text
 */
export const getPaymentStatusText = (status) => {
  switch (status) {
    case 'paid':
      return '已繳交';
    case 'pending':
      return '待繳交';
    case 'overdue':
      return '逾期';
    default:
      return '未知';
  }
};
